import { DragEvent, FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dispatchMeeting, getUploadUrl } from "../lib/api";
import { decideUploadCleanup } from "../lib/orphanRecovery";
import { supabase } from "../lib/supabase";
import { getDefaultWorkspace, Workspace } from "../lib/workspace";
import {
  getLastProjectChoice,
  loadActiveProjects,
  ProjectOption,
  setLastProjectChoice,
  UNASSIGNED_SENTINEL,
} from "../lib/projects";
import { t, tVars } from "../i18n";
import { fileLooksLikeAudio } from "../lib/audioSniff";
import { isMediaFile, putWithProgress } from "../lib/upload";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { Icon } from "../ui/icons";
import { IconButton } from "../ui/IconButton";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";

type Phase = "idle" | "creating" | "uploading" | "dispatching" | "done" | "error";

// Sticky "review context before transcribing" preference, scoped per workspace.
// Same localStorage pattern as lastProjectId (see lib/projects.ts).
// Default: false (opt-in, not opt-out).
const REVIEW_CONTEXT_KEY_PREFIX = "timlul.reviewContextBeforeSoniox.";

function getReviewContextPref(workspaceId: string): boolean {
  try {
    return localStorage.getItem(REVIEW_CONTEXT_KEY_PREFIX + workspaceId) === "true";
  } catch {
    return false;
  }
}

function setReviewContextPref(workspaceId: string, value: boolean): void {
  try {
    localStorage.setItem(REVIEW_CONTEXT_KEY_PREFIX + workspaceId, value ? "true" : "false");
  } catch {
    /* best-effort */
  }
}

// ---------------------------------------------------------------------------
// FileChip — shows selected file name + size with a clear button
// ---------------------------------------------------------------------------

function FileChip({ file, onClear }: { file: File; onClear: () => void }) {
  const sizeMb = (file.size / 1_000_000).toFixed(1);
  return (
    <div
      data-testid="file-chip"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        border: "1px solid var(--line-2)",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-2)",
        fontSize: "var(--text-sm)",
      }}
    >
      <Icon name="audio" size={16} aria-hidden />
      <span dir="auto" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--ink)" }}>
        {file.name}
      </span>
      <span style={{ color: "var(--ink-3)", flexShrink: 0 }}>{sizeMb} MB</span>
      <IconButton
        data-testid="file-chip-clear"
        label={t("upload.clearFile")}
        name="x"
        size={14}
        onClick={onClear}
        style={{ marginInlineStart: 2 }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dropzone
// ---------------------------------------------------------------------------

interface DropzoneProps {
  file: File | null;
  onFile: (f: File) => void;
  onClear: () => void;
  dropError: string | null;
  onDropError: (msg: string | null) => void;
}

function Dropzone({ file, onFile, onClear, dropError, onDropError }: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const f = files[0];
    if (!isMediaFile(f)) {
      onDropError(t("upload.dropzoneInvalid"));
      return;
    }
    onDropError(null);
    onFile(f);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  }

  function handleClick() {
    inputRef.current?.click();
  }

  return (
    <div>
      {/* Hidden input — kept for click-to-browse and tests */}
      <input
        ref={inputRef}
        type="file"
        accept="audio/*,video/*"
        style={{ display: "none" }}
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
      />

      {file ? (
        <FileChip file={file} onClear={onClear} />
      ) : (
        <div
          data-testid="dropzone"
          role="button"
          tabIndex={0}
          aria-label={t("upload.dropzoneTitle")}
          onClick={handleClick}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClick(); }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragActive ? "var(--accent)" : "var(--line-2)"}`,
            borderRadius: "var(--radius-md)",
            background: dragActive ? "var(--accent-soft)" : "var(--surface-2)",
            padding: "32px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            transition: "border-color 0.15s, background 0.15s",
          }}
        >
          <Icon name="upload" size={28} aria-hidden style={{ color: "var(--ink-3)" }} />
          <p style={{ margin: 0, fontWeight: 600, color: "var(--ink)", fontSize: "var(--text-md)" }}>
            {t("upload.dropzoneTitle")}
          </p>
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: "var(--text-sm)" }}>
            {t("upload.dropzoneSubtitle")}
          </p>
        </div>
      )}

      {dropError && (
        <p
          data-testid="dropzone-error"
          style={{ margin: "6px 0 0", fontSize: "var(--text-sm)", color: "var(--danger)" }}
        >
          {dropError}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

// Project picker semantics (Slice 4, 2026-05-24):
// - "Unassigned" is a first-class option (the sentinel UNASSIGNED_SENTINEL
//   in the picker maps to project_id = null on the meeting row).
// - Default is the user's last-used choice for this workspace (sticky via
//   localStorage). On a fresh workspace with no prior choice the picker
//   defaults to Unassigned.
// - If the last-used project has been soft-deleted (or simply doesn't
//   exist), the picker falls back to Unassigned silently.
// - Live subtitle under the picker tells the user which attendees +
//   glossary will flow into their meeting, so they understand the
//   consequence of the choice before submitting.

export function MeetingNew() {
  const nav = useNavigate();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectChoice, setProjectChoice] = useState<string>(UNASSIGNED_SENTINEL);
  const [title, setTitle] = useState("");
  const [prose, setProse] = useState("");
  const [recordedAt, setRecordedAt] = useState<string>(() =>
    new Date().toISOString().slice(0, 16),
  );
  const [file, setFile] = useState<File | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);
  const [sniffWarning, setSniffWarning] = useState(false);
  // Sequence token guarding handlePickedFile's async sniff against a stale
  // verdict landing after a later pick (fix-wave 2026-07-15).
  const sniffSeq = useRef(0);
  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Pre-Soniox context review toggle. Default false (opt-in). Sticky per
  // workspace via localStorage — frequent users who always want to review
  // don't have to remember to tick it each time.
  const [reviewContextBeforeSoniox, setReviewContextBeforeSoniox] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDefaultWorkspace()
      .then(async (ws) => {
        if (cancelled) return;
        setWorkspace(ws);
        const active = await loadActiveProjects(ws.id);
        if (cancelled) return;
        setProjects(active);
        // Sticky default: respect last-used choice, but only if it still
        // refers to an active project (or the Unassigned sentinel).
        const last = getLastProjectChoice(ws.id);
        if (last === UNASSIGNED_SENTINEL) {
          setProjectChoice(UNASSIGNED_SENTINEL);
        } else if (last && active.some((p) => p.id === last)) {
          setProjectChoice(last);
        } else {
          setProjectChoice(UNASSIGNED_SENTINEL);
        }
        // Restore the review-context preference for this workspace.
        setReviewContextBeforeSoniox(getReviewContextPref(ws.id));
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedProject =
    projectChoice === UNASSIGNED_SENTINEL
      ? null
      : projects.find((p) => p.id === projectChoice) ?? null;

  function handlePickedFile(f: File) {
    setFile(f);
    setSniffWarning(false);
    // Match → silent; no match → soft warning, file stays accepted
    // (warn-but-allow, spec 2026-07-14 §4). Guard against stale verdicts
    // (a fast second pick landing before the first sniff resolves) with a
    // sequence token, and swallow rejections (unreadable file): stay
    // silent — warn-but-allow never blocks.
    const seq = ++sniffSeq.current;
    fileLooksLikeAudio(f)
      .then((ok) => {
        if (sniffSeq.current === seq) setSniffWarning(!ok);
      })
      .catch(() => {
        /* unreadable file: stay silent — warn-but-allow never blocks */
      });
  }

  function clearFile() {
    setFile(null);
    setDropError(null);
    setSniffWarning(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!workspace || !file) return;
    setError(null);

    const projectId =
      projectChoice === UNASSIGNED_SENTINEL ? null : projectChoice;

    let meetingId: string | null = null;
    let audioInR2 = false;
    try {
      // 1. Get a signed PUT URL + object_key FIRST, so the row can be born
      //    already carrying its audio key (enables clean rollback).
      setPhase("creating");
      const upload = await getUploadUrl({
        workspace_id: workspace.id,
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        size_bytes: file.size,
      });

      // 2. Insert the meeting row (status=pending) carrying r2_object_key.
      const { data: created, error: insertErr } = await supabase
        .from("meetings")
        .insert({
          workspace_id: workspace.id,
          project_id: projectId,
          title: title || file.name,
          prose,
          recorded_at: recordedAt ? new Date(recordedAt).toISOString() : null,
          audio_mime_type: file.type || "application/octet-stream",
          audio_size_bytes: file.size,
          engine: workspace.default_engine,
          status: "pending",
          review_context_before_soniox: reviewContextBeforeSoniox,
          r2_object_key: upload.object_key,
        })
        .select("id")
        .single();
      if (insertErr) throw insertErr;
      meetingId = created!.id as string;

      // Sticky-default writes.
      setLastProjectChoice(workspace.id, projectChoice);
      setReviewContextPref(workspace.id, reviewContextBeforeSoniox);

      // 3. PUT the file directly to R2.
      setPhase("uploading");
      await putWithProgress(upload.url, upload.headers, file, setProgress);
      audioInR2 = true;

      // 4. Tell the worker to pick it up.
      setPhase("dispatching");
      await dispatchMeeting(meetingId);

      setPhase("done");
      nav(`/meetings/${meetingId}`);
    } catch (err) {
      const cleanup = decideUploadCleanup({
        rowCreated: meetingId !== null,
        audioInR2,
      });
      if (cleanup === "mark_failed" && meetingId) {
        // Audio is safe in R2; processing didn't start. Keep the row, mark it
        // so the meeting page offers a durable "Try again" (no re-upload).
        try {
          // supabase-js returns { error } instead of throwing on a PostgREST
          // failure — check it explicitly, else we'd nav to a row still
          // 'pending' where "Try again" never appears.
          const { error: markErr } = await supabase
            .from("meetings")
            .update({
              status: "error",
              error_message: "Processing didn't start — press Try again.",
            })
            .eq("id", meetingId);
          if (markErr) throw markErr;
          nav(`/meetings/${meetingId}`);
          return;
        } catch {
          /* fall through to inline error */
        }
      } else if (cleanup === "mark_failed_no_audio" && meetingId) {
        // The audio never landed, but the row carries the user's typed
        // context (title, notes, project) — the expensive part. Keep it,
        // clear the dangling audio reference, and mark it failed so the
        // meeting page offers "Replace the audio file". (2026-07-14
        // reversal of the rollback-delete.)
        try {
          const { error: markErr } = await supabase
            .from("meetings")
            .update({
              status: "error",
              r2_object_key: null,
              error_message: t("upload.errorUploadNotFinished"),
            })
            .eq("id", meetingId);
          if (markErr) throw markErr;
          nav(`/meetings/${meetingId}`);
          return;
        } catch {
          /* fall through to inline error */
        }
      }
      setPhase("error");
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const busy = phase !== "idle" && phase !== "error";

  if (error && phase !== "error") {
    return (
      <div data-testid="page-error" className="card">
        <p className="error">{error}</p>
      </div>
    );
  }
  if (!workspace) return <p className="muted">{t("common.loading")}</p>;

  return (
    <>
      {/* Page header consistent with library */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: "var(--space-5)" }}>
        <h2 style={{ margin: 0, fontSize: "var(--text-xl)", fontWeight: 700, color: "var(--ink)" }}>
          {t("upload.pageTitle")}
        </h2>
      </div>

      <form onSubmit={handleSubmit}>
        <Card style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>

          {/* ---- Dropzone ---- */}
          <div className="field">
            <Dropzone
              file={file}
              onFile={handlePickedFile}
              onClear={clearFile}
              dropError={dropError}
              onDropError={setDropError}
            />
            {sniffWarning && (
              <p
                data-testid="sniff-warning"
                style={{ margin: "6px 0 0", fontSize: "var(--text-sm)", color: "var(--warning, #b45309)" }}
              >
                {t("upload.sniffWarning")}
              </p>
            )}
          </div>

          {/* ---- Title ---- */}
          <div className="field">
            <label htmlFor="title" style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: "var(--text-sm)", color: "var(--ink-2)" }}>
              {t("upload.titleLabel")}
            </label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("upload.titlePlaceholder")}
            />
          </div>

          {/* ---- Project ---- */}
          <div className="field">
            <label htmlFor="project" style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: "var(--text-sm)", color: "var(--ink-2)" }}>
              {t("upload.projectLabel")}
            </label>
            <Select
              id="project"
              value={projectChoice}
              onChange={(e) => setProjectChoice(e.target.value)}
            >
              <option value={UNASSIGNED_SENTINEL}>{t("nav.unassigned")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
            <p className="muted" style={{ fontSize: "var(--text-sm)", marginTop: 4 }}>
              {selectedProject
                ? tVars("upload.projectHelper", { name: selectedProject.name })
                : t("upload.projectHelperNone")}
            </p>
          </div>

          {/* ---- When recorded ---- */}
          <div className="field">
            <label htmlFor="recordedAt" style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: "var(--text-sm)", color: "var(--ink-2)" }}>
              {t("upload.recordedAtLabel")}
            </label>
            <input
              id="recordedAt"
              type="datetime-local"
              value={recordedAt}
              onChange={(e) => setRecordedAt(e.target.value)}
              className="input-kit"
              style={{ maxWidth: 280 }}
            />
            <p className="muted" style={{ fontSize: "var(--text-sm)", marginTop: 4 }}>
              {t("upload.recordedAtHelper")}
            </p>
          </div>

          {/* ---- Context ---- */}
          <div className="field">
            <label htmlFor="prose" style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: "var(--text-sm)", color: "var(--ink-2)" }}>
              {t("upload.contextLabel")}
            </label>
            <textarea
              id="prose"
              value={prose}
              onChange={(e) => setProse(e.target.value)}
              placeholder={t("upload.contextPlaceholder")}
              className="input-kit"
              rows={4}
              style={{ resize: "vertical" }}
            />
            <p className="muted" style={{ fontSize: "var(--text-sm)", marginTop: 4 }}>
              {t("upload.contextHelper")}
            </p>
          </div>

          {/* ---- Review context toggle ---- */}
          <div
            className="field"
            style={{ borderTop: "1px solid var(--border)", paddingTop: "var(--space-3)", marginTop: 0 }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={reviewContextBeforeSoniox}
                onChange={(e) => setReviewContextBeforeSoniox(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <span>
                <strong style={{ fontSize: "var(--text-sm)" }}>
                  {t("upload.reviewContextLabel")}
                </strong>
                <br />
                <span className="muted" style={{ fontSize: "var(--text-sm)" }}>
                  {t("upload.reviewContextHelper")}
                </span>
              </span>
            </label>
          </div>

          {/* ---- Error inline ---- */}
          {phase === "error" && error && (
            <p data-testid="page-error" style={{ color: "var(--danger)", fontSize: "var(--text-sm)", margin: 0 }}>
              {error}
            </p>
          )}

          {/* ---- Upload progress bar ---- */}
          {phase === "uploading" && (
            <div
              style={{
                height: 4,
                background: "var(--line)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${progress}%`,
                  background: "var(--accent)",
                  borderRadius: 2,
                  transition: "width 0.2s",
                }}
              />
            </div>
          )}

          {/* ---- Submit button ---- */}
          <Button
            type="submit"
            variant="primary"
            disabled={!file || busy}
          >
            {busy ? phaseLabel(phase, progress) : t("upload.submitIdle")}
          </Button>

        </Card>
      </form>
    </>
  );
}

function phaseLabel(p: Phase, progress: number): string {
  if (p === "creating") return t("upload.submitCreating");
  if (p === "uploading") return tVars("upload.uploadingPercent", { progress });
  if (p === "dispatching") return t("upload.submitDispatching");
  if (p === "done") return t("upload.submitDone");
  return "…";
}

